import type React from 'react'
import { ArrowUp, ArrowDown, ArrowUpDown, RotateCcw } from 'lucide-react'
import { GROUP_HEADER, HEADER_CLASSES } from '../../../lib/table-utils'

export interface OptionChainHeaderProps {
    isVisible: (key: string) => boolean
    sortKey?: string
    sortDirection?: 'asc' | 'desc'
    onSort?: (key: string) => void
    onResetSort?: () => void
}

export const OptionChainHeader: React.FC<OptionChainHeaderProps> = ({
    isVisible,
    sortKey = 'strike',
    sortDirection = 'desc',
    onSort,
    onResetSort,
}) => {
    // Helper helper to reduce boilerplate
    const show = (key: string) => isVisible(key)

    return (
        <thead className="sticky top-0 z-50 group">
            {/* Top Row: Group Headers */}
            {/* Dynamic ColSpan Calculation */}

            {(() => {
                const callCols = [
                    'delta',
                    'gamma',
                    'theta',
                    'vega',
                    'oi',
                    'oi_change',
                    'volume',
                    'vol_oi',
                    'vol_oi_chng',
                    'iv',
                    'change_p',
                    'ltp',
                    'ce_support_prem',
                    'ce_poc_prem',
                    'ce_res_prem',
                    'combined_oi',
                    'combined_oi_change',
                    'combined_oi_change_p',
                    'vol_diff',
                    'oi_diff',
                ]
                const putCols = [
                    'combined_vol_change',
                    'combined_vol_change_p',
                    'combined_volume',
                    'ltp',
                    'pe_support_prem',
                    'pe_poc_prem',
                    'pe_res_prem',
                    'change_p',
                    'iv',
                    'vol_oi_chng',
                    'vol_oi',
                    'volume',
                    'oi_change',
                    'oi',
                    'vega',
                    'theta',
                    'gamma',
                    'delta',
                ]
                const callSpan = callCols.filter((c) => show(c)).length
                const putSpan = putCols.filter((c) => show(c)).length

                return (
                    <tr>
                        <th colSpan={callSpan} className={GROUP_HEADER.CALLS_SIDE}>
                            <div className={GROUP_HEADER.BADGE_CALLS}>CALLS</div>
                        </th>
                        <th className="p-1.5 text-center bg-slate-100 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 min-w-[100px]">
                            {onResetSort && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onResetSort()
                                    }}
                                    disabled={sortKey === 'strike' && sortDirection === 'desc'}
                                    className={`inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all duration-150 ${
                                        sortKey === 'strike' && sortDirection === 'desc'
                                            ? 'opacity-40 cursor-not-allowed text-slate-400 dark:text-slate-500'
                                            : 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 shadow-xs border border-indigo-200 dark:border-indigo-800 cursor-pointer active:scale-95'
                                    }`}
                                    title="Reset sort to Strike (Highest to Lowest)"
                                >
                                    <RotateCcw size={10} className="shrink-0" />
                                    <span>Reset</span>
                                </button>
                            )}
                        </th>
                        <th colSpan={putSpan} className={GROUP_HEADER.PUTS_SIDE}>
                            <div className={GROUP_HEADER.BADGE_PUTS}>PUTS</div>
                        </th>
                    </tr>
                )
            })()}

            {/* Bottom Row: Column Headers - MUST MATCH BODY ORDER EXACTLY */}
            <tr>
                {/* CALLS SIDE */}
                {show('delta') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_delta')}
                        isSorted={sortKey === 'ce_delta'}
                        sortDirection={sortDirection}
                    >
                        Delta
                    </GhostTh>
                )}
                {show('gamma') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_gamma')}
                        isSorted={sortKey === 'ce_gamma'}
                        sortDirection={sortDirection}
                    >
                        Gamma
                    </GhostTh>
                )}
                {show('theta') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_theta')}
                        isSorted={sortKey === 'ce_theta'}
                        sortDirection={sortDirection}
                    >
                        Theta
                    </GhostTh>
                )}
                {show('vega') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_vega')}
                        isSorted={sortKey === 'ce_vega'}
                        sortDirection={sortDirection}
                    >
                        Vega
                    </GhostTh>
                )}
                {show('oi') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_oi')}
                        isSorted={sortKey === 'ce_oi'}
                        sortDirection={sortDirection}
                    >
                        OI (L)
                    </GhostTh>
                )}
                {show('oi_change') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_oi_change')}
                        isSorted={sortKey === 'ce_oi_change'}
                        sortDirection={sortDirection}
                    >
                        OI Chg
                    </GhostTh>
                )}
                {show('volume') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_volume')}
                        isSorted={sortKey === 'ce_volume'}
                        sortDirection={sortDirection}
                    >
                        Vol (Cr)
                    </GhostTh>
                )}
                {show('vol_oi') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_vol_oi')}
                        isSorted={sortKey === 'ce_vol_oi'}
                        sortDirection={sortDirection}
                    >
                        Vol-OI
                    </GhostTh>
                )}
                {show('vol_oi_chng') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_vol_oi_chng')}
                        isSorted={sortKey === 'ce_vol_oi_chng'}
                        sortDirection={sortDirection}
                    >
                        Vol-OI-Chng
                    </GhostTh>
                )}
                {show('iv') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_iv')}
                        isSorted={sortKey === 'ce_iv'}
                        sortDirection={sortDirection}
                    >
                        IV
                    </GhostTh>
                )}
                {show('change_p') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_change_p')}
                        isSorted={sortKey === 'ce_change_p'}
                        sortDirection={sortDirection}
                    >
                        Chg%
                    </GhostTh>
                )}
                {show('ltp') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('ce_ltp')}
                        isSorted={sortKey === 'ce_ltp'}
                        sortDirection={sortDirection}
                    >
                        LTP
                    </GhostTh>
                )}
                {show('ce_support_prem') && (
                    <GhostTh
                        align="right"
                        side="call"
                        color="green"
                        onClick={() => onSort?.('ce_support_prem')}
                        isSorted={sortKey === 'ce_support_prem'}
                        sortDirection={sortDirection}
                    >
                        CE Supp
                    </GhostTh>
                )}
                {show('ce_poc_prem') && (
                    <GhostTh
                        align="right"
                        side="call"
                        color="blue"
                        onClick={() => onSort?.('ce_poc_prem')}
                        isSorted={sortKey === 'ce_poc_prem'}
                        sortDirection={sortDirection}
                    >
                        CE POC
                    </GhostTh>
                )}
                {show('ce_res_prem') && (
                    <GhostTh
                        align="right"
                        side="call"
                        color="red"
                        onClick={() => onSort?.('ce_res_prem')}
                        isSorted={sortKey === 'ce_res_prem'}
                        sortDirection={sortDirection}
                    >
                        CE Resist
                    </GhostTh>
                )}
                {show('combined_oi') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('combined_oi')}
                        isSorted={sortKey === 'combined_oi'}
                        sortDirection={sortDirection}
                    >
                        Comb OI
                    </GhostTh>
                )}
                {show('combined_oi_change') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('combined_oi_change')}
                        isSorted={sortKey === 'combined_oi_change'}
                        sortDirection={sortDirection}
                    >
                        Comb OI Chg
                    </GhostTh>
                )}
                {show('combined_oi_change_p') && (
                    <GhostTh
                        align="right"
                        side="call"
                        onClick={() => onSort?.('combined_oi_change_p')}
                        isSorted={sortKey === 'combined_oi_change_p'}
                        sortDirection={sortDirection}
                    >
                        Comb OI Chg%
                    </GhostTh>
                )}
                {show('vol_diff') && (
                    <GhostTh
                        align="center"
                        side="neutral"
                        color="blue"
                        onClick={() => onSort?.('vol_diff')}
                        isSorted={sortKey === 'vol_diff'}
                        sortDirection={sortDirection}
                    >
                        Vol Diff
                    </GhostTh>
                )}
                {show('oi_diff') && (
                    <GhostTh
                        align="center"
                        side="neutral"
                        color="blue"
                        onClick={() => onSort?.('oi_diff')}
                        isSorted={sortKey === 'oi_diff'}
                        sortDirection={sortDirection}
                    >
                        OI Diff
                    </GhostTh>
                )}

                {/* STRIKE */}
                <th
                    className={`${HEADER_CLASSES.STRIKE_STICKY} cursor-pointer hover:bg-slate-200/80 dark:hover:bg-slate-700/80 select-none transition-colors group/th ${
                        sortKey === 'strike'
                            ? 'bg-indigo-50/90 dark:bg-indigo-950/50 border-b-2 border-indigo-500/80 text-indigo-600 dark:text-indigo-400'
                            : ''
                    }`}
                    onClick={() => onSort?.('strike')}
                    title="Sort by Strike (Click to toggle Ascending / Descending)"
                >
                    <div className="inline-flex items-center justify-center gap-1 w-full">
                        <span>STRIKE</span>
                        {sortKey === 'strike' ? (
                            sortDirection === 'asc' ? (
                                <ArrowUp size={11} className="text-indigo-600 dark:text-indigo-400 stroke-[2.5]" />
                            ) : (
                                <ArrowDown size={11} className="text-indigo-600 dark:text-indigo-400 stroke-[2.5]" />
                            )
                        ) : (
                            <ArrowUpDown size={10} className="text-slate-400 dark:text-slate-500 opacity-0 group-hover/th:opacity-100 transition-opacity" />
                        )}
                    </div>
                </th>

                {/* PUTS SIDE - Reverse Order relative to center */}
                {show('combined_vol_change') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('combined_vol_change')}
                        isSorted={sortKey === 'combined_vol_change'}
                        sortDirection={sortDirection}
                    >
                        Comb Vol Chg
                    </GhostTh>
                )}
                {show('combined_vol_change_p') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('combined_vol_change_p')}
                        isSorted={sortKey === 'combined_vol_change_p'}
                        sortDirection={sortDirection}
                    >
                        Comb Vol Chg%
                    </GhostTh>
                )}
                {show('combined_volume') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('combined_volume')}
                        isSorted={sortKey === 'combined_volume'}
                        sortDirection={sortDirection}
                    >
                        Comb Vol
                    </GhostTh>
                )}
                {show('ltp') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_ltp')}
                        isSorted={sortKey === 'pe_ltp'}
                        sortDirection={sortDirection}
                    >
                        LTP
                    </GhostTh>
                )}
                {show('pe_support_prem') && (
                    <GhostTh
                        align="left"
                        side="put"
                        color="green"
                        onClick={() => onSort?.('pe_support_prem')}
                        isSorted={sortKey === 'pe_support_prem'}
                        sortDirection={sortDirection}
                    >
                        PE Supp
                    </GhostTh>
                )}
                {show('pe_poc_prem') && (
                    <GhostTh
                        align="left"
                        side="put"
                        color="blue"
                        onClick={() => onSort?.('pe_poc_prem')}
                        isSorted={sortKey === 'pe_poc_prem'}
                        sortDirection={sortDirection}
                    >
                        PE POC
                    </GhostTh>
                )}
                {show('pe_res_prem') && (
                    <GhostTh
                        align="left"
                        side="put"
                        color="red"
                        onClick={() => onSort?.('pe_res_prem')}
                        isSorted={sortKey === 'pe_res_prem'}
                        sortDirection={sortDirection}
                    >
                        PE Resist
                    </GhostTh>
                )}
                {show('change_p') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_change_p')}
                        isSorted={sortKey === 'pe_change_p'}
                        sortDirection={sortDirection}
                    >
                        Chg%
                    </GhostTh>
                )}
                {show('iv') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_iv')}
                        isSorted={sortKey === 'pe_iv'}
                        sortDirection={sortDirection}
                    >
                        IV
                    </GhostTh>
                )}
                {show('vol_oi_chng') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_vol_oi_chng')}
                        isSorted={sortKey === 'pe_vol_oi_chng'}
                        sortDirection={sortDirection}
                    >
                        Vol-OI-Chng
                    </GhostTh>
                )}
                {show('vol_oi') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_vol_oi')}
                        isSorted={sortKey === 'pe_vol_oi'}
                        sortDirection={sortDirection}
                    >
                        Vol-OI
                    </GhostTh>
                )}
                {show('volume') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_volume')}
                        isSorted={sortKey === 'pe_volume'}
                        sortDirection={sortDirection}
                    >
                        Vol (Cr)
                    </GhostTh>
                )}
                {show('oi_change') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_oi_change')}
                        isSorted={sortKey === 'pe_oi_change'}
                        sortDirection={sortDirection}
                    >
                        OI Chg
                    </GhostTh>
                )}
                {show('oi') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_oi')}
                        isSorted={sortKey === 'pe_oi'}
                        sortDirection={sortDirection}
                    >
                        OI (L)
                    </GhostTh>
                )}
                {show('vega') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_vega')}
                        isSorted={sortKey === 'pe_vega'}
                        sortDirection={sortDirection}
                    >
                        Vega
                    </GhostTh>
                )}
                {show('theta') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_theta')}
                        isSorted={sortKey === 'pe_theta'}
                        sortDirection={sortDirection}
                    >
                        Theta
                    </GhostTh>
                )}
                {show('gamma') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_gamma')}
                        isSorted={sortKey === 'pe_gamma'}
                        sortDirection={sortDirection}
                    >
                        Gamma
                    </GhostTh>
                )}
                {show('delta') && (
                    <GhostTh
                        align="left"
                        side="put"
                        onClick={() => onSort?.('pe_delta')}
                        isSorted={sortKey === 'pe_delta'}
                        sortDirection={sortDirection}
                    >
                        Delta
                    </GhostTh>
                )}
            </tr>
        </thead>
    )
}

interface GhostThProps {
    children: React.ReactNode
    align?: 'left' | 'right' | 'center'
    side?: 'call' | 'put' | 'neutral'
    color?: 'red' | 'green' | 'blue' | 'amber' | 'purple' | 'neutral' // Flexible colors
    onClick?: () => void
    className?: string
    colSpan?: number
    rowSpan?: number
    topOffset?: string
    isSorted?: boolean
    sortDirection?: 'asc' | 'desc'
}

export const GhostTh: React.FC<GhostThProps> = ({
    children,
    align = 'center',
    side = 'neutral',
    color,
    onClick,
    className = '',
    colSpan,
    rowSpan,
    topOffset = '46px',
    isSorted = false,
    sortDirection = 'desc',
}) => {
    // Premium light-themed header styles
    let thBgClass = isSorted
        ? 'bg-indigo-50/90 dark:bg-indigo-950/50 backdrop-blur-md border-b-2 border-indigo-500/80 shadow-xs'
        : 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/80'
    let textClass = 'text-slate-400 dark:text-slate-500 font-black'

    // Side logic - Clean text colors
    if (isSorted) {
        textClass = 'text-indigo-600 dark:text-indigo-400 font-black'
    } else if (side === 'call' || color === 'red') {
        textClass = 'text-rose-600 dark:text-rose-400 font-black'
    } else if (side === 'put' || color === 'green') {
        textClass = 'text-emerald-600 dark:text-emerald-400 font-black'
    } else if (color === 'blue' || color === 'purple') {
        textClass = 'text-indigo-600 dark:text-indigo-400 font-black'
    } else if (color === 'amber') {
        textClass = 'text-amber-600 dark:text-amber-400 font-black'
    }

    const alignClass =
        align === 'left' ? 'text-left pl-3 justify-start' : align === 'right' ? 'text-right pr-3 justify-end' : 'text-center justify-center'

    return (
        <th
            className={`p-0 sticky z-30 whitespace-nowrap group/th ${thBgClass} ${className}`}
            onClick={onClick}
            colSpan={colSpan}
            rowSpan={rowSpan}
            style={{
                cursor: onClick ? 'pointer' : 'default',
                top: topOffset, // Fixed position below first header row
            }}
        >
            <div className={`py-2.5 flex items-center gap-1 select-none transition-colors ${alignClass}`}>
                <span className={`text-[10px] tracking-widest uppercase ${textClass}`}>
                    {children}
                </span>
                {onClick && (
                    <span className="inline-flex items-center shrink-0">
                        {isSorted ? (
                            sortDirection === 'asc' ? (
                                <ArrowUp size={11} className="text-indigo-600 dark:text-indigo-400 stroke-[2.5]" />
                            ) : (
                                <ArrowDown size={11} className="text-indigo-600 dark:text-indigo-400 stroke-[2.5]" />
                            )
                        ) : (
                            <ArrowUpDown size={10} className="text-slate-400 dark:text-slate-500 opacity-0 group-hover/th:opacity-100 transition-opacity" />
                        )}
                    </span>
                )}
            </div>
        </th>
    )
}
